import { ref } from "vue";
import type { Ref } from "vue";
import {
  UpdateBulkDeliveryCapMutation,
  UpdateBulkDeliveryCapMutationVariables,
  UpdateBulkDeliveryCapDocument,
  UpdateBulkDeliveryGroupCapMutation,
  UpdateBulkDeliveryGroupCapMutationVariables,
  UpdateBulkDeliveryGroupCapDocument,
  type DeliveryCapUpdateBulkInput,
  type DeliveryGroupCapUpdateBulkInput,
} from "@/generated/graphql";
import {
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";
import type {
  ListData,
  ListGroupData,
  ChainSelectValue,
  ShopSelectValue,
  DeliveryGroupSelectValue,
  DeliveryCapSettingActionsReturn,
} from "../types";

/**
 * 編集/保存/バリデーション（店舗キャップ・グループキャップ共通）
 */
export const useDeliveryCapSettingActions = (params: {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  validErrdialog: Ref<boolean>;
  capMessage: Ref<string>;
  overlay: Ref<boolean>;
  processFlag: Ref<number>;
  confirmDialog: Ref<boolean>;
  completeDialog: Ref<boolean>;
  mode: Ref<string>;
  chainSelectValues: Ref<ChainSelectValue>;
  shopSelectValues: Ref<ShopSelectValue>;
  deliveryGroupSelectValues: Ref<DeliveryGroupSelectValue>;
  mappedListData: Map<string, ListData[]>;
  mappedListGroupData: Map<string, ListGroupData[]>;
  getGroupCapOrders: () => Promise<void>;
}): DeliveryCapSettingActionsReturn => {
  const {
    validErrdialog,
    capMessage,
    overlay,
    processFlag,
    confirmDialog,
    completeDialog,
    mode,
    chainSelectValues,
    shopSelectValues,
    deliveryGroupSelectValues,
    mappedListData,
    mappedListGroupData,
    getGroupCapOrders,
  } = params;

  const deliveryCapUpdateBulkInput = ref<DeliveryCapUpdateBulkInput[]>([]);
  const deliveryGroupCapUpdateBulkInput = ref<DeliveryGroupCapUpdateBulkInput[]>([]);
  const updateBulkDeliveryCapMutation = ref<UpdateBulkDeliveryCapMutation | null>(null);
  const updateBulkDeliveryGroupCapMutation = ref<UpdateBulkDeliveryGroupCapMutation | null>(null);

  // 編集前データのバックアップ（キャンセル時に復元するため）
  let backupMappedListData: Map<string, ListData[]> = new Map();
  let backupMappedListGroupData: Map<string, ListGroupData[]> = new Map();

  /**
   * Map のディープコピーを作成する
   */
  const deepCopyMap = <T>(source: Map<string, T[]>): Map<string, T[]> => {
    const copy = new Map<string, T[]>();
    source.forEach((value, key) => {
      copy.set(key, JSON.parse(JSON.stringify(value)));
    });
    return copy;
  };

  /**
   * バックアップから Map を復元する
   */
  const restoreMap = <T>(target: Map<string, T[]>, backup: Map<string, T[]>): void => {
    target.clear();
    backup.forEach((value, key) => {
      target.set(key, JSON.parse(JSON.stringify(value)));
    });
  };

  /**
   * 編集ボタン @click
   */
  const onClickEdit = (): void => {
    // 編集前のデータをバックアップ
    backupMappedListData = deepCopyMap(mappedListData);
    backupMappedListGroupData = deepCopyMap(mappedListGroupData);
    // 編集フラグをONにする
    processFlag.value = 1;
    console.log(processFlag.value);
  };

  /**
   * 更新ボタン @click
   */
  const onClickEditConfirm = (): void => {
    // 更新確認ダイアログを表示する
    confirmDialog.value = true;
    console.log(mappedListData);
  };

  /**
   * キャンセルボタン @click
   */
  const onClickCancel = (): void => {
    // バックアップからデータを復元して変更前の状態に戻す
    restoreMap(mappedListData, backupMappedListData);
    restoreMap(mappedListGroupData, backupMappedListGroupData);
    // 編集フラグをOFFにする
    processFlag.value = 0;
  };

  /**
   * 更新確認ダイアログ 更新ボタン @click
   */
  const onClickEditOK = async (): Promise<void> => {
    // 更新確認ダイアログを非表示にする
    confirmDialog.value = false;
    overlay.value = true;
    deliveryCapUpdateBulkInput.value = [];

    if (mode.value === "shop") {
      // 登録更新する
      mappedListData.forEach((_area_code_value, _area_code) => {
        _area_code_value.forEach((_area_code_key_value) => {
          var _cap_data_tmp = {
            customer_code: localStorageGetCustomerCode(),
            chain_code: chainSelectValues.value!.chain_code,
            shop_code: shopSelectValues.value!.shop_code,
            area_code: _area_code,
            delivery_id: _area_code_key_value.delivery_id,
            delivery_date: "",
            cap: 0,
            user_id: localStorageGetUserId(),
          };

          for (const _prop of Object.keys(_area_code_key_value)) {
            if (_prop.substring(0, 10) === "date_data_") {
              _cap_data_tmp.delivery_date =
                _prop.substring(10, 14) +
                "-" +
                _prop.substring(14, 16) +
                "-" +
                _prop.substring(16, 18);
              _cap_data_tmp.cap = _area_code_key_value[_prop].cap_count;

              if (
                typeof _area_code_key_value[_prop].cap_count !== "number" ||
                _area_code_key_value[_prop].cap_count > 999 ||
                _area_code_key_value[_prop].length === 0
              ) {
                overlay.value = false;
                validErrdialog.value = true;
                capMessage.value =
                  "キャップ数は半角数字3文字までで入力してください";
              }

              if (
                deliveryCapUpdateBulkInput.value !== null &&
                deliveryCapUpdateBulkInput.value !== undefined
              ) {
                deliveryCapUpdateBulkInput.value.push({
                  customer_code: localStorageGetCustomerCode(),
                  chain_code: chainSelectValues.value!.chain_code,
                  shop_code: shopSelectValues.value!.shop_code,
                  area_code: _area_code,
                  delivery_id: _area_code_key_value.delivery_id,
                  delivery_date: _cap_data_tmp.delivery_date,
                  cap: _cap_data_tmp.cap,
                  user_id: localStorageGetUserId(),
                });
              }
            }
          }
        });
      });

      if (!validErrdialog.value) {
        // GraphQL ミューテーション
        const apolloClient = await generateApolloClient();
        const result = await apolloClient.mutate<
          UpdateBulkDeliveryCapMutation,
          UpdateBulkDeliveryCapMutationVariables
        >({
          mutation: UpdateBulkDeliveryCapDocument,
          variables: {
            input: deliveryCapUpdateBulkInput.value as DeliveryCapUpdateBulkInput[],
          },
        });
        console.log(result.data);
        updateBulkDeliveryCapMutation.value = result.data;

        // 編集フラグON、完了ダイアログ表示
        processFlag.value = 0;
        overlay.value = false;
        completeDialog.value = true;
      }
    } else {
      deliveryGroupCapUpdateBulkInput.value = [];

      mappedListGroupData.forEach((_group_area_value) => {
        _group_area_value.forEach((_detail_value) => {
          if (_detail_value.group_flag) {
            // グループキャップ
            console.log(_detail_value);

            let _group_delivery_date = "";
            let _group_cap = "";

            for (const _prop of Object.keys(_detail_value)) {
              if (_prop.substring(0, 10) === "date_data_") {
                _group_delivery_date =
                  _prop.substring(10, 14) +
                  "-" +
                  _prop.substring(14, 16) +
                  "-" +
                  _prop.substring(16, 18);
                _group_cap = _detail_value[_prop].group_cap;

                if (
                  typeof _detail_value[_prop].group_cap !== "number" ||
                  _detail_value[_prop].group_cap > 999 ||
                  _detail_value[_prop].length === 0
                ) {
                  overlay.value = false;
                  validErrdialog.value = true;
                  capMessage.value =
                    "グループキャップ数は半角数字3文字までで入力してください";
                }

                if (deliveryGroupCapUpdateBulkInput.value) {
                  deliveryGroupCapUpdateBulkInput.value.push({
                    customer_code: localStorageGetCustomerCode(),
                    chain_code: chainSelectValues.value.chain_code,
                    group_code: deliveryGroupSelectValues.value.group_code,
                    group_delivery_id: _detail_value.group_delivery_id,
                    delivery_date: _group_delivery_date,
                    cap: Number(_group_cap),
                    user_id: localStorageGetUserId(),
                  });
                }
              }
            }
          } else if (_detail_value.area_flag) {
            // 店舗・便キャップ
            let _delivery_date = "";
            let _cap = "";

            for (const _prop of Object.keys(_detail_value)) {
              if (_prop.substring(0, 10) === "date_data_") {
                _delivery_date =
                  _prop.substring(10, 14) +
                  "-" +
                  _prop.substring(14, 16) +
                  "-" +
                  _prop.substring(16, 18);
                _cap = _detail_value[_prop].cap;

                if (
                  typeof _detail_value[_prop].cap !== "number" ||
                  _detail_value[_prop].cap > 999 ||
                  _detail_value[_prop].length === 0
                ) {
                  overlay.value = false;
                  validErrdialog.value = true;
                  capMessage.value =
                    "キャップ数は半角数字3文字までで入力してください";
                }

                if (deliveryCapUpdateBulkInput.value) {
                  deliveryCapUpdateBulkInput.value.push({
                    customer_code: localStorageGetCustomerCode(),
                    chain_code: chainSelectValues.value.chain_code,
                    shop_code: _detail_value.shop_code ?? "",
                    area_code: _detail_value.area_code ?? "",
                    delivery_id: _detail_value.delivery_id ?? 0,
                    delivery_date: _delivery_date,
                    cap: Number(_cap),
                    user_id: localStorageGetUserId(),
                  });
                }
              }
            }
          }
        });
      });

      if (!validErrdialog.value) {
        // graphql接続
        const apolloClient = await generateApolloClient();

        const result_area = await apolloClient.mutate<
          UpdateBulkDeliveryCapMutation,
          UpdateBulkDeliveryCapMutationVariables
        >({
          mutation: UpdateBulkDeliveryCapDocument,
          variables: {
            input: (deliveryCapUpdateBulkInput.value ?? [])
              .filter((item) => item !== null && item !== undefined)
              .map((item) => item as DeliveryCapUpdateBulkInput),
          },
        });
        updateBulkDeliveryCapMutation.value = result_area.data;

        const result_group = await apolloClient.mutate<
          UpdateBulkDeliveryGroupCapMutation,
          UpdateBulkDeliveryGroupCapMutationVariables
        >({
          mutation: UpdateBulkDeliveryGroupCapDocument,
          variables: {
            input: (deliveryGroupCapUpdateBulkInput.value ?? [])
              .filter((item) => item !== null && item !== undefined)
              .map((item) => item as DeliveryGroupCapUpdateBulkInput),
          },
        });
        updateBulkDeliveryGroupCapMutation.value = result_group.data;

        await getGroupCapOrders();

        // 編集フラグをOFFにする
        processFlag.value = 0;
        overlay.value = false;
        completeDialog.value = true;
      }
    }
  };

  return {
    deliveryCapUpdateBulkInput,
    deliveryGroupCapUpdateBulkInput,
    updateBulkDeliveryCapMutation,
    updateBulkDeliveryGroupCapMutation,
    onClickEdit,
    onClickEditConfirm,
    onClickCancel,
    onClickEditOK,
  };
};
